import { useState, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
}

const THRESHOLD = 80;
const MAX_PULL = 120;

type PullState = 'idle' | 'pulling' | 'triggered' | 'refreshing';

export function PullToRefresh({ onRefresh, children }: PullToRefreshProps) {
  const [pullState, setPullState] = useState<PullState>('idle');
  const [pullDistance, setPullDistance] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const currentYRef = useRef(0);
  const isPullingRef = useRef(false);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (pullState === 'refreshing') return;

      const container = containerRef.current;
      if (!container) return;

      // Only activate when scrolled to top
      if (container.scrollTop > 0) return;

      startYRef.current = e.touches[0].clientY;
      isPullingRef.current = true;
    },
    [pullState],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isPullingRef.current || pullState === 'refreshing') return;

      const container = containerRef.current;
      if (!container) return;

      // Re-check scroll position in case user scrolled during the gesture
      if (container.scrollTop > 0) {
        isPullingRef.current = false;
        setPullDistance(0);
        setPullState('idle');
        return;
      }

      currentYRef.current = e.touches[0].clientY;
      const delta = currentYRef.current - startYRef.current;

      if (delta <= 0) {
        setPullDistance(0);
        setPullState('idle');
        return;
      }

      // Apply resistance: the further you pull, the harder it gets
      const resistance = 0.5;
      const distance = Math.min(delta * resistance, MAX_PULL);

      // Prevent native overscroll while pulling
      if (distance > 0) {
        e.preventDefault();
      }

      setPullDistance(distance);
      setPullState(distance >= THRESHOLD ? 'triggered' : 'pulling');
    },
    [pullState],
  );

  const handleTouchEnd = useCallback(async () => {
    if (!isPullingRef.current) return;
    isPullingRef.current = false;

    if (pullState === 'triggered') {
      setPullState('refreshing');
      setPullDistance(THRESHOLD);

      try {
        await onRefresh();
      } catch {
        // Swallow - consumer handles errors
      }

      setPullState('idle');
      setPullDistance(0);
    } else {
      setPullState('idle');
      setPullDistance(0);
    }
  }, [pullState, onRefresh]);

  const rotation = Math.min((pullDistance / THRESHOLD) * 360, 360);
  const opacity = Math.min(pullDistance / (THRESHOLD * 0.5), 1);

  return (
    <div
      ref={containerRef}
      className="relative h-full overflow-y-auto"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: pullState === 'idle' ? 'auto' : 'pan-x' }}
    >
      {/* Pull indicator */}
      <div
        className="flex flex-col items-center justify-end overflow-hidden transition-[height] duration-200 ease-out"
        style={{
          height: pullDistance,
          transitionDuration: pullState === 'pulling' || pullState === 'triggered' ? '0ms' : '200ms',
        }}
      >
        <div className="flex flex-col items-center gap-1.5 pb-3" style={{ opacity }}>
          <RefreshCw
            className="h-5 w-5 text-gray-400"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: pullState === 'refreshing' ? 'none' : undefined,
              animation: pullState === 'refreshing' ? 'spin 0.8s linear infinite' : undefined,
              color: pullState === 'triggered' || pullState === 'refreshing' ? 'hsl(221, 83%, 53%)' : undefined,
            }}
          />
          <span className="text-xs text-gray-400 font-medium">
            {pullState === 'refreshing'
              ? 'Atualizando...'
              : pullState === 'triggered'
                ? 'Solte para atualizar'
                : 'Puxe para atualizar'}
          </span>
        </div>
      </div>

      {children}
    </div>
  );
}
